import i18n from "@/i18n";
import { useTranslation } from "react-i18next";
import { useDeleteTemplateResource } from "@/models/projectTemplates/projectTemplateEditorLifecycle";
import { loader, useLoadedData } from "./loader";
import { useBoolState } from "@/hooks/useBoolState";
import { useFormattedTimePreferences } from "@/hooks/useFormattedTimePreferences";
import { useRichTextHandlers } from "@/hooks/useRichTextHandlers";
import { buildProjectTemplateResourceNavigation } from "@/models/projectTemplates/pageNavigation";
import { useTemplateComments } from "@/models/projectTemplates/useTemplateComments";
import { usePaths } from "@/routes/paths";
import type { PageModule } from "@/routes/types";
import { DocumentPage, IconEdit, IconTrash, showErrorToast } from "turboui";
import React from "react";
import { useNavigate } from "react-router";

export default { name: "ProjectTemplateDocumentPage", loader, Page } as PageModule;

function Page() {
  const { t } = useTranslation();
  const { template, node, comments } = useLoadedData();
  const deleteResourceMutation = useDeleteTemplateResource({ templateId: template.id, spaceId: template.space.id });
  const paths = usePaths();
  const navigate = useNavigate();
  const formattedTimePreferences = useFormattedTimePreferences();
  const [showDeleteModal, toggleDeleteModal] = useBoolState(false);
  const document = node.document;
  const canEdit = !template.archivedAt && Boolean(template.permissions?.canEdit || template.permissions?.hasFullAccess);
  const richTextHandlers = useRichTextHandlers({
    scope: { type: "space", id: template.space.id },
    templateComments: true,
    taskList: { resourceType: "template_document", resourceId: document.id, field: "content", canEdit },
  });
  const docsAndFilesLink = paths.projectTemplatePath(template.id, { tab: "docs-and-files" });
  const commentsProps = useTemplateComments({
    templateId: template.id,
    parentType: "document",
    parentId: document.id,
    comments,
    canEdit,
    richTextHandlers,
    formattedTimePreferences,
  });

  async function handleDelete() {
    try {
      await deleteResourceMutation.mutateAsync({ templateId: template.id, nodeId: node.id });
      navigate(docsAndFilesLink);
    } catch {
      showErrorToast(i18n.t("Resource not deleted"), i18n.t("The document is still on this page. Try again."));
    }
  }

  return (
    <DocumentPage
      pageTitle={[document.name, template.name]}
      navigation={buildProjectTemplateResourceNavigation(template, paths, { parentFolderId: node.parentFolderId })}
      options={[
        {
          type: "link",
          icon: IconEdit,
          label: t("Edit"),
          link: paths.projectTemplateEditDocumentPath(template.id, node.id),
          keepOutsideOnBigScreen: true,
          testId: "edit-document-link",
        },
        {
          type: "action",
          icon: IconTrash,
          label: t("Delete"),
          onClick: toggleDeleteModal,
          hidden: !canEdit,
          testId: "delete-resource-link",
        },
      ]}
      testId="project-template-document-page"
      title={document.name}
      author={document.author ?? null}
      state="published"
      publishedAt={document.insertedAt}
      modifiedAt={document.updatedAt}
      formattedTimePreferences={formattedTimePreferences}
      content={document.content}
      mentionedPersonLookup={richTextHandlers.mentionedPersonLookup}
      taskList={richTextHandlers.taskList}
      hideDraftActions
      hideReactions
      comments={commentsProps}
      hideSubscriptions
      hideCopyModal
      deleteModal={{
        isOpen: showDeleteModal,
        onClose: toggleDeleteModal,
        documentName: document.name,
        onConfirm: handleDelete,
      }}
    />
  );
}
